import { faPencil, faQuestionCircle, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Observer, observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { Billable, Event, ICreditCardSource, IStripe } from '@feathr/blackbox';
import { isBankAccountSource, isCreditCardSource } from '@feathr/blackbox';
import type { IColumn } from '@feathr/components';
import {
  Address,
  Button,
  Card,
  Chip,
  Collapse,
  Skeleton,
  TableColumnHeader,
  Tooltip,
} from '@feathr/components';
import { BankAccount, CreditCard } from '@feathr/extender/components/BillingSource';
import { BillListTable } from '@feathr/extender/components/BillTable';
import { EventsTable } from '@feathr/extender/components/EventsTable';
import { useAccount, useLocalUrl } from '@feathr/extender/state';
import type { TCountryCodes } from '@feathr/hooks';
import { useToggle } from '@feathr/hooks';

import BillableDeleteModal from './BillableDeleteModal';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as styles from './BillableCard.css';

interface IProps {
  allowDelete?: boolean;
  allowEdit?: boolean;
  billable?: Billable;
  className?: string;
  isLoading?: boolean;
  redirect?: string;
}

function BillableCard({
  billable,
  className,
  isLoading,
  redirect,
  allowEdit = true,
  allowDelete = false,
}: IProps): JSX.Element | null {
  const account = useAccount();
  const localUrl = useLocalUrl();
  const [isDeleteModalOpen, toggleIsDeleteModalOpen] = useToggle(false);
  const { t } = useTranslation();
  if (!billable) {
    return null;
  }

  const isReallyloading = isLoading || billable.isPending;
  const address = billable.get('address');
  const source = billable.get('stripe', {} as IStripe).source;
  const id = billable.id;
  const editLink = localUrl(`/settings/billing/configurations/${id}/edit`);
  const filters = {
    account: account.id,
    billable: billable.id,
  };
  const actions: JSX.Element[] = [];
  if (allowEdit) {
    actions.push(
      <>
        {isReallyloading ? (
          <Button disabled={true} key={'edit'} type={'naked'}>
            <FontAwesomeIcon icon={faPencil} />
          </Button>
        ) : (
          <Tooltip key={'edit'} title={t('Edit')}>
            <Button
              link={
                redirect
                  ? {
                      pathname: editLink,
                      search: `?redirect=${encodeURIComponent(redirect)}`,
                    }
                  : editLink
              }
              type={'naked'}
            >
              <FontAwesomeIcon icon={faPencil} />
            </Button>
          </Tooltip>
        )}
      </>,
    );
  }
  if (allowDelete) {
    actions.push(
      <>
        {isReallyloading ? (
          <Button disabled={true} key={'delete'} type={'naked'}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        ) : (
          <Tooltip key={'delete'} title={t('Delete billing configuration')}>
            <Button onClick={toggleIsDeleteModalOpen} type={'naked'}>
              <FontAwesomeIcon icon={faTrash} />
            </Button>
            {isDeleteModalOpen && (
              <BillableDeleteModal billable={billable} onClose={toggleIsDeleteModalOpen} />
            )}
          </Tooltip>
        )}
      </>,
    );
  }

  const columns: Array<IColumn<Event>> = [
    {
      id: 'name',
      Header: TableColumnHeader({
        sortType: 'alpha',
        title: 'Name',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => (
              <Link to={localUrl(original.getItemUrl())}>{original.get('name')}</Link>
            )}
          </Observer>
        );
      },
    },
  ];

  return (
    <Card
      actions={actions}
      className={classNames(styles.root, className)}
      data-id={id}
      data-model={'Billable'}
      element={'article'}
      loading={isReallyloading}
      raised={true}
      title={
        <span className={styles.title}>
          {billable.get('name')}
          <Link to={localUrl('/settings/billing/license')}>
            {account.get('license').billable === id && (
              <Chip theme={'blue'} tooltip={t("This is the Account's billing configuration.")}>
                {t('Account')}
              </Chip>
            )}
          </Link>
        </span>
      }
    >
      <div className={styles.description}>{billable.get('description')}</div>
      <Card
        className={styles.billTo}
        loading={isReallyloading}
        nested={true}
        secondary={true}
        title={t('Bill to')}
      >
        {isReallyloading ? (
          <Skeleton className={styles.billToSkeleton} paragraphs={5} />
        ) : (
          <>
            <div>{billable.get('name')}</div>
            <div className={styles.email}>{billable.get('email')}</div>
            {!!address && (
              <Address
                administrative_area_name={address.state}
                className={styles.address}
                country_code={address.country as TCountryCodes}
                locality={address.city}
                postal_code={address.postal_code}
                premise1={address.line1}
                premise2={address.line2}
              />
            )}
          </>
        )}
      </Card>
      <Card
        className={styles.paymentMethods}
        loading={isReallyloading}
        nested={true}
        secondary={true}
        title={t('Payment Method')}
      >
        {isReallyloading && (
          <Skeleton
            className={styles.paymentMethodSkeleton}
            paragraphs={2}
            prefix={true}
            width={300}
          />
        )}
        {!!source &&
          !!source.id &&
          (isBankAccountSource(source) ? (
            <BankAccount billable={billable} key={source.id} />
          ) : isCreditCardSource(source) ? (
            <CreditCard key={source.id} source={source as ICreditCardSource} />
          ) : null)}
        {!isReallyloading && (!source || !source.id) && (
          <p className={styles.empty}>{t('Payment method has not been set.')}</p>
        )}
      </Card>
      <Collapse title={t('Projects and Invoices')}>
        <div className={styles.content}>
          <Card
            className={styles.projects}
            nested={true}
            secondary={true}
            title={
              <span>
                {t('Projects')}
                <Tooltip
                  className={styles.icon}
                  title={t(
                    'This billing configuration is the default billing configuration for the following projects.',
                  )}
                >
                  <FontAwesomeIcon icon={faQuestionCircle} />
                </Tooltip>
              </span>
            }
          >
            <EventsTable
              columns={columns}
              filters={{
                is_archived__ne: true,
                billing__billable_id: billable.get('id'),
              }}
            />
          </Card>
          <Card
            className={styles.invoices}
            nested={true}
            secondary={true}
            title={
              <span>
                {t('Invoices')}
                <Tooltip
                  className={styles.icon}
                  title={t(
                    'This billing configuration is the billing configuration attached to the following invoices.',
                  )}
                >
                  <FontAwesomeIcon icon={faQuestionCircle} />
                </Tooltip>
              </span>
            }
          >
            <BillListTable filters={filters} key={'invoices'} noDataText={t('No Invoices')} />
          </Card>
        </div>
      </Collapse>
    </Card>
  );
}

export default observer(BillableCard);
